"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Preparedness() {
  const {
    ref: copyRef,
    isVisible: copyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    id: 1,
    label: "Refill anytime"
  }, {
    id: 2,
    label: "Travel-ready size"
  }, {
    id: 3,
    label: "Family-safe formula"
  }];
  return <section data-section-id="5824" id="the-preparedness-hub" className="relative bg-background py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={copyRef} className="lg:col-span-6 order-2 lg:order-1" style={{
          opacity: copyVisible ? 1 : 0,
          transform: copyVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 600ms cubic-bezier(.2,.7,.2,1), transform 600ms cubic-bezier(.2,.7,.2,1)"
        }}>
            <div style={{
            opacity: copyVisible ? 1 : 0,
            transform: copyVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 600ms cubic-bezier(.2,.7,.2,1) 0ms, transform 600ms cubic-bezier(.2,.7,.2,1) 0ms"
          }}>
              <span className="inline-block font-default text-xs md:text-[0.8125rem] font-bold uppercase tracking-[0.1em] text-accent-foreground">
                Before You Enter™
              </span>
            </div>

            <h1 className="mt-6 font-serif font-medium text-foreground" style={{
            fontSize: "clamp(2.75rem, 6.5vw, 5.25rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.025em",
            opacity: copyVisible ? 1 : 0,
            transform: copyVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 600ms cubic-bezier(.2,.7,.2,1) 80ms, transform 600ms cubic-bezier(.2,.7,.2,1) 80ms"
          }}>
              Prepared for{" "}
              <em className="italic font-normal text-primary">peace</em>{" "}
              of mind.
            </h1>

            <p className="mt-6 font-default text-foreground/80 max-w-xl" style={{
            fontSize: "clamp(1.0625rem, 1.3vw, 1.25rem)",
            lineHeight: 1.55,
            opacity: copyVisible ? 1 : 0,
            transform: copyVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 600ms cubic-bezier(.2,.7,.2,1) 160ms, transform 600ms cubic-bezier(.2,.7,.2,1) 160ms"
          }}>
              B.Y.E. Hygiene Mist™ is your portable companion for life's daily transitions. Simple, effective, and designed to keep your family protected wherever you go.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" style={{
            opacity: copyVisible ? 1 : 0,
            transform: copyVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 600ms cubic-bezier(.2,.7,.2,1) 240ms, transform 600ms cubic-bezier(.2,.7,.2,1) 240ms"
          }}>
              <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-6 h-auto font-default font-semibold text-base shadow-md transition-transform hover:scale-[1.03] active:scale-[0.98]" style={{
              transitionTimingFunction: "cubic-bezier(.34,1.56,.64,1)",
              transitionDuration: "220ms"
            }}>
                <Link to="/ShopPage">Shop the mist</Link>
              </Button>

              <Link to="/OurStoryPage" className="group inline-flex items-center gap-2 font-default font-medium text-foreground">
                <span className="relative">
                  Our story
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-primary origin-left scale-x-100 transition-transform duration-[240ms] ease-out group-hover:scale-x-100" />
                </span>
                <ArrowRight className="w-4 h-4 text-primary transition-transform duration-[240ms] group-hover:translate-x-1" />
              </Link>
            </div>

            <ul className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2" style={{
            opacity: copyVisible ? 1 : 0,
            transform: copyVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 600ms cubic-bezier(.2,.7,.2,1) 320ms, transform 600ms cubic-bezier(.2,.7,.2,1) 320ms"
          }}>
              {benefits.map((benefit, i) => <li key={benefit.id} data-index={i} className="flex items-center gap-x-5 font-default text-sm text-foreground/70">
                  {i > 0 && <span aria-hidden="true" className="w-1 h-1 rounded-lg bg-border" />}
                  <span>{benefit.label}</span>
                </li>)}
            </ul>
          </div>

          {}
          <div ref={imgRef} className="lg:col-span-6 order-1 lg:order-2 relative" style={{
          opacity: imgVisible ? 1 : 0,
          transform: imgVisible ? "scale(1)" : "scale(0.98)",
          transition: "opacity 700ms ease-out, transform 700ms ease-out"
        }}>
            {}
            <div aria-hidden="true" className="absolute -bottom-8 -right-6 md:-bottom-12 md:-right-10 w-2/3 h-2/3 bg-accent rounded-lg -z-0" style={{
            borderRadius: "62% 38% 55% 45% / 50% 60% 40% 50%",
            animation: "bloomDrift 16s ease-in-out infinite"
          }} />
            <div aria-hidden="true" className="absolute -top-6 -left-4 md:-top-10 md:-left-8 w-1/3 h-1/3 bg-secondary/20 -z-0" style={{
            borderRadius: "55% 45% 60% 40% / 45% 55% 45% 55%",
            animation: "bloomDrift 18s ease-in-out infinite reverse"
          }} />

            <div className="relative z-10 overflow-hidden rounded-lg shadow-lg">
              <div className="aspect-[4/5] w-full bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/bye_hygiene_mist_lifestyle.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/bye_hygiene_mist_lifestyle.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/bye_hygiene_mist_lifestyle.png-wvc-sizes" alt="Parent using B.Y.E. Hygiene Mist before entering home" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bloomDrift {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(10px, -8px) rotate(2deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="bloomDrift"] { animation: none !important; }
        }
      `}</style>
    </section>;
}